import { useCallback, useMemo, useRef, useState } from 'react'
import type { AnyGameModule, SessionStats } from '../core/contracts'
import { createRng } from '../core/rng'
import { sessionPace } from '../core/contracts'
import { initialDifficulty, nextDifficulty } from '../core/difficulty'
import type { DifficultyState } from '../core/difficulty'
import { summarize } from '../core/scoring'
import type { RoundLog } from '../core/scoring'
import { haptics } from '../platform/telegram'

/**
 * Оболочка игры.
 *
 * Монтирует любой GameModule, ведёт цикл раундов, крутит сложность,
 * копит лог и отдаёт итог наверх. Про конкретные игры не знает ничего -
 * работает только через контракт.
 *
 * Именно этот компонент избавляет игры от дублирования: таймер,
 * счётчик раундов, адаптив и сбор статистики написаны один раз.
 */

interface Props {
  game: AnyGameModule
  /** База для детерминизма. Один seed - одна и та же тренировка. */
  seed?: number
  /** Стартовый уровень из сохранённого прогресса. */
  startLevel?: number
  /** Личный темп игрока; игра растягивает под него тайминги. */
  pace?: number
  onFinish: (stats: SessionStats, log: readonly RoundLog[]) => void
}

export function GameHost({ game, seed = Date.now(), startLevel, pace = 1, onFinish }: Props) {
  const rng = useMemo(() => createRng(seed), [seed])

  const [difficulty, setDifficulty] = useState<DifficultyState>(() =>
    startLevel === undefined
      ? initialDifficulty(game)
      : { level: startLevel, roundsAtLevel: 0 },
  )
  const [roundNo, setRoundNo] = useState(0)
  const [round, setRound] = useState(() =>
    game.generateRound(
      startLevel ?? game.difficulty.initial,
      rng,
      sessionPace([], pace, game.referenceRtMs),
    ),
  )

  const log = useRef<RoundLog[]>([])
  const startedAt = useRef(performance.now())
  // времена верных ответов в этом заходе - основа для подстройки темпа
  const observed = useRef<number[]>([])

  const handleAnswer = useCallback(
    (answer: unknown) => {
      const outcome = game.evaluate(round, answer)
      const rtMs = outcome.rtMs > 0 ? outcome.rtMs : performance.now() - startedAt.current

      log.current.push({
        correct: outcome.correct,
        accuracy: outcome.accuracy,
        rtMs,
        level: difficulty.level,
      })
      if (outcome.correct) observed.current.push(rtMs)

      if (outcome.correct) haptics.correct()
      else haptics.wrong()

      const step = nextDifficulty(difficulty, outcome.accuracy, game.difficulty)
      if (step.change === 'up') haptics.levelUp()

      const done = roundNo + 1 >= game.roundsPerSession
      if (done) {
        onFinish(summarize(log.current), log.current)
        return
      }

      // темп пересчитываем каждый раунд: первые идут мягче,
      // дальше игра подстраивается под сегодняшнюю скорость игрока
      const nextPace = sessionPace(observed.current, pace, game.referenceRtMs)

      setDifficulty(step.state)
      setRoundNo((n) => n + 1)
      setRound(game.generateRound(step.state.level, rng, nextPace))
      startedAt.current = performance.now()
    },
    [game, round, difficulty, roundNo, rng, pace, onFinish],
  )

  const { View } = game

  return (
    <View
      round={round}
      level={difficulty.level}
      onAnswer={handleAnswer}
      key={roundNo}
    />
  )
}
